import { FC, memo, useCallback, useMemo, useState } from "react";
import { ICourse } from "../../../domain/models/Course";
import PopupMenu from "../../modals/PopupMenu";
import { useTranslation } from "react-i18next";
import DeleteTrashIconSVG from "../../../custom-components/svg/functional-icons/DeleteTrashIconSVG";
import BaseModal from "../../modals/BaseModal";
import EditPencilIconSVG from "../../../custom-components/svg/functional-icons/EditPencilIconSVG";
import ImagePlaceholderSVG from "../../shared/svg/ImagePlaceholder";
import MenuItem from "../../modals/MenuItem";
import { FILES_URL } from "../../../Config";

interface CoursesCardProps {
    item: ICourse;
    onDelete?: (id: string) => void;
    onEdit?: (id: string) => void;
}

const CoursesCard: FC<CoursesCardProps> = ({ item, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const createdDate = useMemo(() => new Date(item.createdAt).toLocaleDateString(), [item.createdAt]);
    const firstTranslation = useMemo(() => item.translations?.[0], [item.translations]);

    const handleCopyId = useCallback(() => {
        navigator.clipboard.writeText(item.id).catch(() => {
            console.error("Failed to copy ID to clipboard");
        });
    }, [item.id]);

    const handleDelete = useCallback(() => {
        onDelete?.(item.id);
        setDeleteModalVisible(false);
    }, [onDelete, item.id]);

    const openDeleteModal = useCallback(() => setDeleteModalVisible(true), []);

    return (
        <div className="bg-white dark:bg-dark-bg-100 rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition relative h-[360px] flex flex-col">
            <div className="flex justify-center flex-shrink-0">
                {item.files && item.files.length > 0 ? (
                    <img
                        src={item.files[0].dataUrl || `${FILES_URL}/${item.files[0].filePath}`}
                        alt={firstTranslation?.title || "Course"}
                        className="w-full h-40 object-cover"
                    />
                ) : (
                    <ImagePlaceholderSVG fill="#bec6d5" classname="h-40 justify-self-center" />
                )}
            </div>

            <div className="p-4 flex flex-col flex-1 overflow-hidden">
                <h2 className="text-lg font-semibold text-light-text-100 dark:text-dark-text-100 mb-2 truncate">
                    {firstTranslation?.title}
                </h2>
                <p className="text-light-text-100 dark:text-dark-text-300 text-sm mb-1 line-clamp-2">
                    {firstTranslation?.shortContent}
                </p>
                <div className="mt-1">
                    <p className="text-xs text-gray-500 mb-1 truncate">
                        {t("console.courses.table.topic")}: {firstTranslation?.topic}
                    </p>
                    <p className="text-xs text-gray-500 mb-1 truncate">
                        {t("console.courses.table.author")}: {firstTranslation?.author}
                    </p>
                    {firstTranslation?.duration && (
                        <p className="text-xs text-gray-500 mb-1 truncate">
                            {t("console.courses.table.duration")}: {firstTranslation?.duration}
                        </p>
                    )}
                    <span className="text-xs text-gray-400">{createdDate}</span>
                </div>
            </div>
            <div className="absolute top-2 right-2 z-10">
                <PopupMenu
                    trigger={
                        <button className="px-3 py-1 text-light-text-100 dark:text-dark-text-100 bg-light-bg-200 dark:bg-dark-bg-700 rounded-lg hover:bg-gray-200 hover:dark:bg-dark-bg-900 shadow">
                            ⋮
                        </button>
                    }
                >
                    <MenuItem
                        onClick={() => onEdit(item.id)}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-2">
                            <EditPencilIconSVG iconColor="black" /> {t("console.courses.edit")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={handleCopyId}
                        className="hover:bg-gray-200 hover:dark:bg-dark-bg-700 hover:dark:text-dark-text-100"
                    >
                        <div className="flex items-center gap-2">
                            <EditPencilIconSVG iconColor="black" /> {t("console.courses.copy")}
                        </div>
                    </MenuItem>

                    <MenuItem
                        onClick={openDeleteModal}
                        className="text-red-500 hover:bg-red-100 hover:dark:bg-dark-bg-600"
                    >
                        <div className="flex items-center gap-2">
                            <DeleteTrashIconSVG iconColor="red" /> {t("console.courses.delete")}
                        </div>
                    </MenuItem>
                </PopupMenu>
            </div>

            <BaseModal
                isVisible={isDeleteModalVisible}
                setIsVisible={setDeleteModalVisible}
                title={t("console.courses.deleteConfirmTitle")}
                confirmTitle={t("console.courses.delete")}
                cancelTitle={t("console.common.cancel")}
                confirmHandler={handleDelete}
                additionalWrapClassName="w-[400px]"
                additionalConfirmButtonClassName="bg-red-600 hover:bg-red-700"
            >
                <div className="text-center py-4">
                    <p className="text-lg text-light-text-100 dark:text-dark-text-100 mb-2">
                        {t("console.courses.deleteConfirmText")}
                    </p>
                    <p className="text-sm text-gray-500">{firstTranslation?.title}</p>
                </div>
            </BaseModal>
        </div>
    );
};

export default memo(CoursesCard);
